import React, { useState, useEffect } from 'react';
import { auth, db } from '../services/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

interface CheckoutPageProps {
  onBack: () => void;
  onSuccess: () => void;
  selectedPlan: 'essential' | 'premium';
}

interface PixData {
  id: number;
  qr_code: string;
  qr_code_base64: string;
  ticket_url: string;
  amount_charged?: number;
  discount_applied?: number;
  coupon_applied?: string | null;
  coupon_valid?: boolean;
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({ onBack, onSuccess, selectedPlan: initialPlan }) => {
  const [loading, setLoading] = useState(false);
  const [coupon, setCoupon] = useState(() => localStorage.getItem('affiliateId') || "");
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly'); // Default Anual para aumentar LTV
  const [pixData, setPixData] = useState<PixData | null>(null);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(599); // 10 minutos de urgência
  const [isPro, setIsPro] = useState(false);
  const [subscribedAt, setSubscribedAt] = useState<Date | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [wantsRenewal, setWantsRenewal] = useState(false);
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const [wasProAtPayment, setWasProAtPayment] = useState<boolean | null>(null);
  const [expiresBeforePayment, setExpiresBeforePayment] = useState<number | null>(null);

  // Checa status PRO ao entrar no checkout para evitar mostrar "pago" antes de gerar Pix
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsub = onSnapshot(doc(db, 'userProgress', user.uid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setIsPro(Boolean(data?.isPro));

        // captura datas para mostrar status atual
        if (data?.subscribedAt?.seconds) {
          setSubscribedAt(new Date(data.subscribedAt.seconds * 1000));
        }
        if (data?.expiresAt?.seconds) {
          setExpiresAt(new Date(data.expiresAt.seconds * 1000));
        }
      }
    });
    return () => unsub();
  }, []);

  // Scroll para o topo ao entrar no checkout (evita iniciar na área do botão)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Timer de Urgência
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Evita auto-sucesso para usuários já PRO: só monitora pagamento se gerar Pix
  useEffect(() => {
    if (!pixData) return;
    const user = auth.currentUser;
    if (!user) return;

    console.log("Monitorando aprovação do pagamento...");
    
    // Fica ouvindo mudanças no documento do usuário após gerar Pix
    const unsubscribe = onSnapshot(doc(db, 'userProgress', user.uid), (docSnap) => {
      if (!awaitingPayment) return;
      if (docSnap.exists()) {
        const data = docSnap.data();
        const expiresSnap = data?.expiresAt?.seconds ?? null;

        // Só confirma se a gente estava aguardando pagamento e detectou mudança relevante
        const isNewPayment = (
          expiresBeforePayment !== null && expiresSnap !== null && expiresSnap !== expiresBeforePayment
        ) || (expiresBeforePayment === null && wasProAtPayment === false && data?.isPro);

        if (data?.isPro && isNewPayment) {
           onSuccess();
        }
      }
    });
    return () => unsubscribe();
  }, [pixData, onSuccess, awaitingPayment, expiresBeforePayment, wasProAtPayment]);

  const plans = {
    essential: {
      name: 'Plano NOMEADO',
      firstMonthPrice: 9.90,
      monthlyPrice: 19.90,
      yearlyPrice: 149.90,
    }
  };

  const currentPlan = plans.essential;
  const totalToday = billingCycle === 'monthly' ? currentPlan.firstMonthPrice : currentPlan.yearlyPrice;
  const [couponInput, setCouponInput] = useState<string>("");
  const [discountedAmount, setDiscountedAmount] = useState<number | null>(null);
  const [appliedDiscountPercent, setAppliedDiscountPercent] = useState<number>(0);
  const [couponApplied, setCouponApplied] = useState<string | null>(null);
  const [couponFeedback, setCouponFeedback] = useState<string>("");

  useEffect(() => {
    // Pré-carrega cupom do localStorage para o input
    const saved = localStorage.getItem('couponCode') || localStorage.getItem('affiliateId') || "";
    if (saved) {
      setCouponInput(saved.toUpperCase());
      setCoupon(saved.toUpperCase());
    }
  }, []);

  useEffect(() => {
    // Mudou ciclo/valor: limpar preview para nova validação
    setAppliedDiscountPercent(0);
    setDiscountedAmount(null);
    setCouponApplied(null);
    setCouponFeedback('');
  }, [billingCycle, totalToday]);

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    setCoupon(code);
    setCouponFeedback('Validando cupom...');
    setCouponApplied(null);
    setAppliedDiscountPercent(0);
    setDiscountedAmount(null);

    if (!code) {
      setCouponFeedback('Digite um cupom para validar.');
      return;
    }

    try {
      localStorage.setItem('couponCode', code);
      localStorage.setItem('affiliateId', code);

      const response = await fetch('https://us-central1-concurseiro-pobre.cloudfunctions.net/api/mercadopago/validate-coupon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ couponCode: code, affiliateId: code, amount: totalToday })
      });
      const data = await response.json();

      if (data?.coupon_valid) {
        setCouponApplied(data.coupon_applied || code);
        setAppliedDiscountPercent(Number(data.discount_applied) || 0);
        setDiscountedAmount(typeof data.amount_charged === 'number' ? data.amount_charged : null);
        setCouponFeedback(`Cupom válido: ${data.coupon_applied || code}`);
      } else {
        setCouponFeedback(`Cupom ${code} inválido ou inativo.`);
      }
    } catch (err) {
      setCouponFeedback('Não foi possível validar o cupom agora.');
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      if (!coupon && typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const fromUrl = urlParams.get('cupom') || urlParams.get('coupon') || urlParams.get('affiliate');
        if (fromUrl) setCoupon(fromUrl.toUpperCase());
      }
      const user = auth.currentUser;
      if (!user) throw new Error("Usuário não autenticado");

      const couponCode = (coupon || localStorage.getItem('affiliateId') || "").toUpperCase() || null;
      const affiliateId = couponCode || null; // backend valida se existe/ativo
      const amountToCharge = totalToday; // envia valor base; backend decide desconto

      if (affiliateId) {
        localStorage.setItem('affiliateId', affiliateId);
      }

      // Guarda estado antes de gerar Pix para detectar renovação
      setWasProAtPayment(isPro);
      setExpiresBeforePayment(expiresAt ? Math.floor(expiresAt.getTime() / 1000) : null);
      setAwaitingPayment(true);

      const response = await fetch('https://us-central1-concurseiro-pobre.cloudfunctions.net/api/mercadopago/create-pix', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email,
          amount: amountToCharge,
          productId: 'plan_nomeado',
          billingCycle,
          description: `Concurseiro Pobre - ${currentPlan.name} (${billingCycle})`,
          affiliateId,
          couponCode
        })
      });

      const data = await response.json();
      if (data.qr_code) {
        setPixData(data);
        const discountPct = Number(data.discount_applied) || 0;
        setAppliedDiscountPercent(discountPct);
        setDiscountedAmount(typeof data.amount_charged === 'number' ? data.amount_charged : null);
        setCouponApplied(data.coupon_applied || null);

        if (couponCode && !data.coupon_valid) {
          alert(`Cupom ${couponCode} inválido ou inativo. O Pix foi gerado sem desconto.`);
        }
      }
      else throw new Error(data.details || data.error || "Erro ao gerar Pix");
    } catch (error: any) {
      alert("Erro ao processar: " + error.message);
      setAwaitingPayment(false);
    } finally {
      setLoading(false);
    }
  };

  // Caso já seja PRO, mostra aviso e exige confirmação antes de gerar Pix para renovar
  // Caso já seja PRO, mostra status atual e exige confirmação antes de gerar Pix para renovar
  if (isPro && !pixData) {
    const formatDate = (d: Date | null) => {
      if (!d) return "—";
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    const today = new Date();
    const daysRemaining = expiresAt ? Math.max(0, Math.ceil((expiresAt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))) : null;

    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center text-center px-6 py-16">
        <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4" aria-live="polite">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <h1 className="text-2xl font-black text-gray-900 mb-2">Você já está NOMEADO</h1>
        <p className="text-gray-600 max-w-md mb-4">Assinatura ativa. Veja seu status atual antes de renovar.</p>

        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3 text-sm text-emerald-900 font-semibold max-w-sm w-full mb-4 flex flex-col gap-1">
          <div className="flex justify-between"><span>Ciclo atual</span><span>Mensal (padrão)</span></div>
          <div className="flex justify-between"><span>Ativado em</span><span>{formatDate(subscribedAt)}</span></div>
          <div className="flex justify-between"><span>Vencimento</span><span>{formatDate(expiresAt)}</span></div>
          {daysRemaining !== null && <div className="flex justify-between"><span>Restam</span><span>{daysRemaining} dia(s)</span></div>}
        </div>

        <label className="flex items-start gap-2 text-left max-w-md text-sm text-gray-700 mb-4">
          <input
            type="checkbox"
            className="mt-1 accent-emerald-500"
            checked={wantsRenewal}
            onChange={(e) => setWantsRenewal(e.target.checked)}
          />
          <span>Quero gerar um Pix para renovar ou alterar meu ciclo de pagamento.</span>
        </label>

        <button
          onClick={() => {
            if (!wantsRenewal || loading) return;
            handlePayment();
          }}
          disabled={!wantsRenewal || loading}
          className={`bg-emerald-500 hover:bg-emerald-600 text-white font-black px-6 py-3 rounded-2xl shadow-lg shadow-emerald-500/20 active:scale-95 w-full max-w-xs ${(!wantsRenewal || loading) ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          {loading ? "Gerando Pix..." : "Gerar Pix para renovar/mudar ciclo"}
        </button>
        <button
          onClick={onBack}
          className="mt-3 text-gray-500 hover:text-gray-700 font-semibold"
        >
          Voltar
        </button>
      </div>
    );
  }

  const copyToClipboard = () => {
    if (pixData?.qr_code) {
      navigator.clipboard.writeText(pixData.qr_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 pb-20">
      {/* Banner de Urgência */}
      <div className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-bold animate-pulse">
        ⚡ OFERTA DE LANÇAMENTO: Garanta seu desconto especial! Expira em {formatTime(timeLeft)}
      </div>

      <div className="max-w-6xl mx-auto px-4 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-emerald-600 transition font-bold">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Voltar
          </button>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white font-black shadow-lg shadow-emerald-500/20">CP</div>
            <span className="font-black text-xl tracking-tight">CONCURSEIRO<span className="text-emerald-500">POBRE</span></span>
          </div>
          <div className="hidden md:block text-xs font-bold text-gray-400 uppercase tracking-widest">Checkout Seguro 🔒</div>
        </div>

        {!pixData ? (
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* LADO ESQUERDO: Venda */}
            <div className="space-y-10">
              <div>
                <h1 className="text-3xl md:text-5xl font-black leading-tight mb-3 text-gray-900">
                  Saia da fila e vire um <span className="text-emerald-500">NOMEADO</span>.
                </h1>
                <p className="text-lg md:text-xl text-gray-700">
                  Pare de perder tempo com limites e anúncios. Tenha o Professor Pobre (IA) ao seu lado 24h por dia.
                </p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs font-black text-gray-600">
                  <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-2 rounded-xl border border-emerald-100">⚡ 24h IA</div>
                  <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-2 rounded-xl border border-emerald-100">📈 +500k questões</div>
                  <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-2 rounded-xl border border-emerald-100">👥 5k+ alunos</div>
                </div>
              </div>

              {/* Tabela Comparativa */}
              <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-lg shadow-emerald-500/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-black text-gray-900 text-lg">Por que virar NOMEADO?</h3>
                  <span className="px-3 py-1 rounded-full text-xs font-black bg-emerald-100 text-emerald-700">+200% de valor</span>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {[ 
                    { icon: '🧠', title: 'Professor Pobre (IA)', desc: 'Explicações ilimitadas e instantâneas', free: 'Limitado', pro: 'Ilimitado' },
                    { icon: '🃏', title: 'Flashcards', desc: 'Revisão com flashcards prontos e ilimitados', free: 'Limitado', pro: 'Ilimitado', pill: 'Sem limite' },
                    { icon: '🎯', title: 'Questões por dia', desc: 'Pratique sem travar no limite diário', free: '10', pro: 'Ilimitado', pill: 'Sem limite' },
                    { icon: '📝', title: 'Simulados de prova', desc: 'Provas completas com gabarito e revisão', free: 'Não possui', pro: 'Sim' },
                    { icon: '📊', title: 'Histórico & Desempenho', desc: 'Estatísticas e progresso detalhados', free: 'Não possui', pro: 'Completo' },
                    { icon: '⚡', title: 'Suporte', desc: 'Atendimento prioritário e rápido', free: 'Básico', pro: 'VIP' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-2xl border border-gray-100 bg-gray-50/50">
                      <div className="text-xl">{item.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-gray-900 text-sm">{item.title}</span>
                          {item.pill && (
                            <span className="text-[10px] font-black uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">{item.pill}</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs font-black">
                          <span className="text-gray-400">CONCURSEIRO: {item.free}</span>
                          <span className="text-emerald-600">NOMEADO: {item.pro}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Depoimento Rápido */}
              <div className="flex gap-4 items-center bg-emerald-50 p-6 rounded-3xl border border-emerald-100">
                <div className="w-12 h-12 bg-emerald-200 rounded-full flex items-center justify-center text-xl">👨‍🎓</div>
                <p className="text-emerald-900 italic text-sm">
                  "O plano Nomeado me ajudou a focar no que realmente cai. A IA explicando meus erros foi o diferencial para eu subir meu rendimento."
                  <br /><span className="font-bold not-italic mt-1 block">— Pedro S., Aprovado no TJ-SP</span>
                </p>
              </div>
            </div>

            {/* LADO DIREITO: Pagamento */}
            <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-emerald-500/10 border border-gray-100 p-8 md:p-10 sticky top-8">
              <div className="flex items-start justify-between gap-3 mb-6">
                <h2 className="text-2xl font-black flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-500 text-white rounded-full flex items-center justify-center text-lg shadow-lg">1</span>
                  Seu Plano NOMEADO
                </h2>
                <div className="flex flex-col items-end text-right">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-600">Preço de lançamento • vagas limitadas</span>
                  <span className="mt-1 inline-flex items-center gap-2 bg-amber-100 text-amber-800 font-black px-3 py-1 rounded-full text-sm shadow-sm">
                    ⏳ {formatTime(timeLeft)}
                  </span>
                </div>
              </div>

              {/* Seletor de Ciclo */}
              <div className="grid grid-cols-1 gap-4 mb-8">
                <button 
                  onClick={() => setBillingCycle('yearly')}
                  className={`relative p-6 rounded-2xl border-2 text-left transition-all hover:scale-[1.02] ${billingCycle === 'yearly' ? 'border-emerald-500 bg-emerald-50/50 ring-4 ring-emerald-500/5' : 'border-gray-200 bg-white hover:border-emerald-300'}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-black text-xl">Plano Anual</span>
                    <span className="bg-emerald-500 text-white text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-tighter animate-bounce">Mais Popular</span>
                  </div>
                  <p className="text-gray-500 text-sm mb-4">Garante acesso por 12 meses inteiros</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm text-gray-400 line-through">R$ 228,80</span>
                    <span className="text-3xl font-black text-emerald-600">R$ 149,90</span>
                    <span className="text-sm text-emerald-600 font-bold">(-35% OFF)</span>
                  </div>
                  <p className="text-[10px] text-emerald-700 font-bold mt-1">Apenas R$ 12,49 por mês!</p>
                </button>

                <button 
                  onClick={() => setBillingCycle('monthly')}
                  className={`p-6 rounded-2xl border-2 text-left transition-all hover:scale-[1.02] ${billingCycle === 'monthly' ? 'border-emerald-500 bg-emerald-50/50' : 'border-gray-200 bg-white hover:border-emerald-300'}`}
                >
                  <span className="font-black text-xl block mb-1">Plano Mensal</span>
                  <p className="text-gray-500 text-sm mb-4">R$ 9,90 no 1º mês (depois R$ 19,90)</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black text-gray-900 font-sans">R$ 9,90</span>
                    <span className="text-sm text-gray-400 font-bold">hoje</span>
                  </div>
                </button>
              </div>

              {/* Cupom */}
              <div className="space-y-2 mb-2">
                <label className="text-sm font-bold text-gray-700">Cupom</label>
                <div className="flex gap-2">
                  <input
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    placeholder="Digite seu cupom"
                    className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition uppercase"
                    disabled={loading}
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={loading || !couponInput.trim()}
                    className="px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-black rounded-xl disabled:opacity-60"
                  >
                    Aplicar
                  </button>
                </div>
                {couponApplied && appliedDiscountPercent > 0 && (
                  <p className="text-sm text-emerald-700 font-bold">Cupom {couponApplied} aplicado: {appliedDiscountPercent}% OFF</p>
                )}
                {!!couponFeedback && !couponApplied && (
                  <p className="text-xs text-gray-600 font-semibold">{couponFeedback}</p>
                )}
              </div>

              {/* Resumo do pagamento */}
              <div className="space-y-2 mb-4 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                <div className="flex items-center justify-between text-sm font-bold text-gray-700">
                  <span>Valor base</span>
                  <span>R$ {totalToday.toFixed(2)}</span>
                </div>
                {discountedAmount !== null && appliedDiscountPercent > 0 && (
                  <div className="flex items-center justify-between text-sm font-bold text-emerald-700">
                    <span>Desconto ({appliedDiscountPercent}%)</span>
                    <span>-R$ {(totalToday - discountedAmount).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-lg font-black text-gray-900">
                  <span>Total hoje</span>
                  <span className="text-emerald-600">R$ {(discountedAmount !== null ? discountedAmount : totalToday).toFixed(2)}</span>
                </div>
              </div>

              {/* Botão de Ação */}
              <div className="space-y-4">
                <button 
                  onClick={handlePayment}
                  disabled={loading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-black text-xl py-6 rounded-2xl transition-all shadow-xl shadow-emerald-500/20 transform active:scale-95 flex items-center justify-center gap-4 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <div className="flex flex-col items-center leading-tight">
                      <span>GARANTIR MINHA VAGA AGORA 🚀</span>
                      <span className="text-sm font-bold text-emerald-100">
                        {discountedAmount !== null ? `Total com desconto: R$ ${discountedAmount.toFixed(2)}` : `Total hoje: R$ ${totalToday.toFixed(2)}`}
                      </span>
                    </div>
                  )}
                </button>
                <div className="text-center text-xs text-gray-500 font-semibold">
                  Dúvidas? <a href="https://wa.me/5521975064950?text=Tenho%20d%C3%BAvidas%20sobre%20o%20checkout" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline">Fale no WhatsApp</a> • <a href="/politica-cancelamento" className="text-emerald-600 underline">Política de cancelamento</a>
                </div>
                <div className="flex items-center justify-center gap-4 opacity-70 grayscale hover:opacity-100 transition">
                  <img
                    src="/pix-logo.svg"
                    alt="Pix"
                    className="h-5"
                  />
                </div>
              </div>

              {/* FAQ e Garantia */}
              <div className="mt-10 pt-10 border-t border-gray-100 grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-amber-50 border border-amber-100 rounded-2xl shadow-sm">
                  <div className="text-2xl mb-1">🛡️</div>
                  <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">Garantia</p>
                  <p className="text-sm font-black text-amber-800">Cancelamento imediato</p>
                  <p className="text-[10px] text-amber-700 mt-1">Sem fidelidade, Pix seguro</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-2xl">
                  <div className="text-2xl mb-1">⚡</div>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Acesso</p>
                  <p className="text-xs font-black text-gray-800">Imediato</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* TELA DE PIX MELHORADA */
          <div className="max-w-xl mx-auto text-center py-10 animate-fade-in">
             <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
             </div>
             <h2 className="text-3xl font-black mb-4">Código Pix Gerado!</h2>
             <p className="text-gray-500 mb-10">Pague agora para liberar seu acesso instantaneamente.</p>

             <div className="bg-white p-6 border-4 border-emerald-50 rounded-[2rem] inline-block mb-10 shadow-2xl">
                <img src={`data:image/jpeg;base64,${pixData.qr_code_base64}`} alt="QR Code Pix" className="w-64 h-64" />
             </div>

             {(() => {
                const charged = pixData.amount_charged || discountedAmount || totalToday;
                const discountPct = pixData.discount_applied || appliedDiscountPercent || 0;
                return (
                  <div className="text-center mb-6 text-sm font-bold text-emerald-700">
                    {discountPct > 0 ? (
                      <div className="space-y-1">
                        <p>Desconto aplicado: {discountPct}%</p>
                        <p className="text-lg text-emerald-800">Total com desconto: R$ {charged.toFixed(2)}</p>
                        <span className="inline-block bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-black">Pague este valor pelo Pix</span>
                      </div>
                    ) : (
                      <p>Total: R$ {charged.toFixed(2)}</p>
                    )}
                  </div>
                );
             })()}

             <div className="space-y-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Ou copie o código abaixo</p>
                <div className="relative group">
                  <input readOnly value={pixData.qr_code} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-6 py-6 text-xs font-mono text-gray-500 pr-32 focus:border-emerald-500 outline-none transition" />
                  <button onClick={copyToClipboard} className="absolute right-3 top-3 bottom-3 bg-emerald-500 text-white px-6 rounded-xl text-sm font-black hover:bg-emerald-600 transition shadow-lg shadow-emerald-500/20 active:scale-95">
                    {copied ? 'COPIADO!' : 'COPIAR'}
                  </button>
                </div>
                
                <div className="flex items-center justify-center gap-3 text-amber-600 bg-amber-50 py-5 rounded-2xl border border-amber-100 mt-6">
                  <div className="w-3 h-3 bg-amber-500 rounded-full animate-ping" />
                  <span className="text-sm font-black">Aguardando seu pagamento...</span>
                </div>
                
                <p className="text-xs text-gray-400 mt-6">Assim que você pagar no app do seu banco, esta tela mudará sozinha em poucos segundos.</p>
             </div>
          </div>
        )}
      </div>

      {/* Footer Minimalista */}
      <div className="mt-20 text-center text-[10px] text-gray-300 uppercase tracking-[0.2em] font-bold">
        Concurseiro Pobre © 2026 • Tecnologia Mercado Pago 100% Segura
      </div>
    </div>
  );
};

export default CheckoutPage;
